import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    ActionIcon,
    Badge,
    Button,
    Card,
    Checkbox,
    Group,
    Loader,
    Modal,
    Select,
    Stack,
    Text,
    TextInput,
    Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconCopy, IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import {
    createOrgRole,
    deleteOrgRole,
    getPermissionCatalog,
    listOrganizations,
    listOrgRoles,
    updateOrgRole,
} from '../../api/resources';
import { PageHeader } from '../../components/PageHeader';
import { organizationLabel } from '../../lib/labels';
import type { OrgRole, PermissionCatalogEntry } from '../../types/api';

type EditorMode = 'create' | 'edit';

type EditorFormValues = {
    mode: EditorMode;
    roleId: string;
    displayName: string;
    description: string;
    permissionKeys: string[];
};

/** Group catalog entries: a single "Views" group, then one group per action resource. */
function groupCatalog(catalog: PermissionCatalogEntry[]): { label: string; entries: PermissionCatalogEntry[] }[] {
    const views = catalog.filter((entry) => entry.type === 'view');
    const actionGroups = new Map<string, PermissionCatalogEntry[]>();
    for (const entry of catalog) {
        if (entry.type !== 'action') {
            continue;
        }
        const resource = entry.key.split(':')[0] ?? 'other';
        const existing = actionGroups.get(resource) ?? [];
        existing.push(entry);
        actionGroups.set(resource, existing);
    }
    const groups: { label: string; entries: PermissionCatalogEntry[] }[] = [];
    if (views.length > 0) {
        groups.push({ label: 'Views', entries: views });
    }
    for (const [resource, entries] of actionGroups) {
        groups.push({
            label: resource.charAt(0).toUpperCase() + resource.slice(1).replace(/_/g, ' '),
            entries,
        });
    }
    return groups;
}

const emptyEditorValues = (): EditorFormValues => ({
    mode: 'create',
    roleId: '',
    displayName: '',
    description: '',
    permissionKeys: [],
});

export function OrgRolesPage() {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [orgId, setOrgId] = useState<string | null>(null);
    const [editorOpen, setEditorOpen] = useState(false);
    const form = useForm<EditorFormValues>({
        initialValues: emptyEditorValues(),
    });

    const orgsQuery = useQuery({
        queryKey: ['lookup', 'organizations'],
        queryFn: () => listOrganizations({ page: 1, limit: 500 }),
        staleTime: 60_000,
    });
    const catalogQuery = useQuery({
        queryKey: ['permission-catalog'],
        queryFn: getPermissionCatalog,
        staleTime: 5 * 60_000,
    });
    const rolesQuery = useQuery({
        queryKey: ['org-roles', orgId],
        queryFn: () => listOrgRoles(orgId as string),
        enabled: Boolean(orgId),
    });

    const orgOptions = useMemo(
        () =>
            (orgsQuery.data?.data ?? []).map((org) => ({
                value: org.id,
                label: organizationLabel(org),
            })),
        [orgsQuery.data],
    );

    const groups = useMemo(
        () => groupCatalog(catalogQuery.data?.permissions ?? []),
        [catalogQuery.data],
    );
    const allKeys = useMemo(
        () => (catalogQuery.data?.permissions ?? []).map((entry) => entry.key),
        [catalogQuery.data],
    );
    const selectedKeys = useMemo(() => new Set(form.values.permissionKeys), [form.values.permissionKeys]);

    const invalidateRoles = (organizationId: string) =>
        void queryClient.invalidateQueries({ queryKey: ['org-roles', organizationId] });

    const createMutation = useMutation({
        mutationFn: (vars: {
            organizationId: string;
            body: { displayName: string; description?: string; permissionKeys: string[] };
        }) => createOrgRole(vars.organizationId, vars.body),
        onSuccess: (_data, vars) => {
            notifications.show({ color: 'green', message: t('features.orgRoles.created') });
            setEditorOpen(false);
            invalidateRoles(vars.organizationId);
        },
        onError: (e: Error) => notifications.show({ color: 'red', message: e.message }),
    });

    const updateMutation = useMutation({
        mutationFn: (vars: {
            organizationId: string;
            roleId: string;
            body: { displayName?: string; description?: string; permissionKeys?: string[] };
        }) => updateOrgRole(vars.organizationId, vars.roleId, vars.body),
        onSuccess: (_data, vars) => {
            notifications.show({ color: 'green', message: t('features.orgRoles.updated') });
            setEditorOpen(false);
            invalidateRoles(vars.organizationId);
        },
        onError: (e: Error) => notifications.show({ color: 'red', message: e.message }),
    });

    const deleteMutation = useMutation({
        mutationFn: (vars: { organizationId: string; roleId: string }) =>
            deleteOrgRole(vars.organizationId, vars.roleId),
        onSuccess: (_data, vars) => {
            notifications.show({ color: 'green', message: t('features.orgRoles.removed') });
            invalidateRoles(vars.organizationId);
        },
        onError: (e: Error) => notifications.show({ color: 'red', message: e.message }),
    });

    const closeEditor = () => {
        setEditorOpen(false);
        form.setValues(emptyEditorValues());
    };

    const openCreate = () => {
        form.setValues(emptyEditorValues());
        setEditorOpen(true);
    };

    const openEdit = (role: OrgRole) => {
        form.setValues({
            mode: 'edit',
            roleId: role.id,
            displayName: role.displayName || role.name,
            description: role.description ?? '',
            permissionKeys: role.permissionKeys ?? [],
        });
        setEditorOpen(true);
    };

    const openClone = (template: OrgRole) => {
        form.setValues({
            mode: 'create',
            roleId: '',
            displayName: t('features.orgRoles.cloneOf', {
                name: template.displayName || template.name,
            }),
            description: template.description ?? '',
            permissionKeys: template.permissionKeys ?? [],
        });
        setEditorOpen(true);
    };

    const toggleKey = (key: string, checked: boolean) => {
        const keys = new Set(form.values.permissionKeys);
        if (checked) {
            keys.add(key);
        } else {
            keys.delete(key);
        }
        form.setFieldValue('permissionKeys', Array.from(keys));
    };

    const submitEditor = () => {
        if (!orgId) {
            return;
        }
        const displayName = form.values.displayName.trim();
        if (displayName.length === 0) {
            notifications.show({ color: 'red', message: t('features.orgRoles.nameRequired') });
            return;
        }
        const body = {
            displayName,
            description: form.values.description.trim() || undefined,
            permissionKeys: form.values.permissionKeys,
        };
        if (form.values.mode === 'create') {
            createMutation.mutate({ organizationId: orgId, body });
            return;
        }
        updateMutation.mutate({
            organizationId: orgId,
            roleId: form.values.roleId,
            body,
        });
    };

    const roles = rolesQuery.data?.roles ?? [];
    const templates = rolesQuery.data?.templates ?? [];
    const saving = createMutation.isPending || updateMutation.isPending;

    return (
        <Stack gap="lg">
            <PageHeader
                title={t('features.orgRoles.title')}
                description={t('features.orgRoles.description')}
                actions={
                    orgId ? (
                        <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
                            {t('features.orgRoles.createAction')}
                        </Button>
                    ) : undefined
                }
            />

            <Select
                label={t('features.orgRoles.selectOrg')}
                placeholder={t('features.orgRoles.selectOrgPlaceholder')}
                data={orgOptions}
                value={orgId}
                onChange={setOrgId}
                searchable
                allowDeselect={false}
                maw={360}
            />

            {!orgId ? (
                <Text c="dimmed">{t('features.orgRoles.selectOrgPrompt')}</Text>
            ) : rolesQuery.isLoading ? (
                <Group justify="center" py="xl">
                    <Loader />
                </Group>
            ) : (
                <Stack gap="lg">
                    <Stack gap="xs">
                        <Title order={4}>{t('features.orgRoles.rolesTitle')}</Title>
                        {roles.length === 0 ? (
                            <Text c="dimmed" size="sm">
                                {t('features.orgRoles.noRoles')}
                            </Text>
                        ) : (
                            roles.map((role) => (
                                <Card key={role.id} withBorder padding="sm" radius="md">
                                    <Group justify="space-between" align="flex-start">
                                        <Stack gap={2}>
                                            <Text fw={600}>{role.displayName || role.name}</Text>
                                            {role.description ? (
                                                <Text size="xs" c="dimmed">
                                                    {role.description}
                                                </Text>
                                            ) : null}
                                            <Text size="xs" c="dimmed">
                                                {t('features.orgRoles.permissionsCount', {
                                                    count: role.permissionKeys?.length ?? 0,
                                                })}
                                            </Text>
                                        </Stack>
                                        <Group gap="xs">
                                            <ActionIcon
                                                variant="subtle"
                                                onClick={() => openEdit(role)}
                                                aria-label={t('common.actions.edit')}
                                            >
                                                <IconPencil size={16} />
                                            </ActionIcon>
                                            <ActionIcon
                                                variant="subtle"
                                                color="red"
                                                loading={
                                                    deleteMutation.isPending &&
                                                    deleteMutation.variables?.roleId === role.id
                                                }
                                                onClick={() =>
                                                    orgId &&
                                                    deleteMutation.mutate({
                                                        organizationId: orgId,
                                                        roleId: role.id,
                                                    })
                                                }
                                                aria-label={t('common.actions.delete')}
                                            >
                                                <IconTrash size={16} />
                                            </ActionIcon>
                                        </Group>
                                    </Group>
                                </Card>
                            ))
                        )}
                    </Stack>

                    <Stack gap="xs">
                        <Title order={4}>{t('features.orgRoles.templatesTitle')}</Title>
                        <Text size="sm" c="dimmed">
                            {t('features.orgRoles.templatesHint')}
                        </Text>
                        {templates.length === 0 ? (
                            <Text c="dimmed" size="sm">
                                {t('features.orgRoles.noTemplates')}
                            </Text>
                        ) : (
                            templates.map((template) => (
                                <Card key={template.id} withBorder padding="sm" radius="md">
                                    <Group justify="space-between" align="flex-start">
                                        <Stack gap={2}>
                                            <Group gap="xs">
                                                <Text fw={600}>{template.displayName || template.name}</Text>
                                                <Badge size="xs" variant="light" color="grape">
                                                    {t('features.orgRoles.systemBadge')}
                                                </Badge>
                                            </Group>
                                            <Text size="xs" c="dimmed">
                                                {t('features.orgRoles.permissionsCount', {
                                                    count: template.permissionKeys?.length ?? 0,
                                                })}
                                            </Text>
                                        </Stack>
                                        <Button
                                            size="xs"
                                            variant="light"
                                            leftSection={<IconCopy size={14} />}
                                            onClick={() => openClone(template)}
                                        >
                                            {t('features.orgRoles.cloneAction')}
                                        </Button>
                                    </Group>
                                </Card>
                            ))
                        )}
                    </Stack>
                </Stack>
            )}

            <Modal
                opened={editorOpen}
                onClose={closeEditor}
                size="lg"
                title={
                    form.values.mode === 'edit'
                        ? t('features.orgRoles.editModalTitle')
                        : t('features.orgRoles.createModalTitle')
                }
            >
                <Stack key={form.values.mode === 'edit' ? form.values.roleId : 'create'}>
                    <TextInput
                        label={t('fields.name')}
                        placeholder={t('features.orgRoles.namePlaceholder')}
                        required
                        {...form.getInputProps('displayName')}
                    />
                    <TextInput label={t('fields.description')} {...form.getInputProps('description')} />

                    <Group justify="space-between" align="center">
                        <Text fw={600}>{t('features.orgRoles.permissions')}</Text>
                        <Group gap="xs">
                            <Button
                                size="compact-xs"
                                variant="subtle"
                                onClick={() => form.setFieldValue('permissionKeys', [...allKeys])}
                            >
                                {t('features.orgRoles.selectAll')}
                            </Button>
                            <Button
                                size="compact-xs"
                                variant="subtle"
                                color="gray"
                                onClick={() => form.setFieldValue('permissionKeys', [])}
                            >
                                {t('features.orgRoles.clearAll')}
                            </Button>
                        </Group>
                    </Group>

                    {groups.map((group) => (
                        <Stack key={group.label} gap={4}>
                            <Text size="xs" tt="uppercase" c="dimmed" fw={700}>
                                {group.label}
                            </Text>
                            {group.entries.map((entry) => (
                                <Checkbox
                                    key={entry.key}
                                    label={entry.label}
                                    description={entry.key}
                                    checked={selectedKeys.has(entry.key)}
                                    onChange={(e) => toggleKey(entry.key, e.currentTarget.checked)}
                                />
                            ))}
                        </Stack>
                    ))}

                    <Group justify="flex-end">
                        <Button variant="default" onClick={closeEditor}>
                            {t('common.actions.cancel')}
                        </Button>
                        <Button loading={saving} onClick={submitEditor}>
                            {t('common.actions.save')}
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </Stack>
    );
}
